import { useEffect, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { cn } from '@/lib/cn'

export interface GuestRoomValues {
  adults: number
  children: number
  rooms: number
  child_ages: number[]
}

interface GuestRoomSelectorProps {
  adults: number
  children: number
  rooms: number
  /** Per-child ages (0–11). Falls back to `children` default-age entries if omitted. */
  childAges?: number[]
  onChange: (values: GuestRoomValues) => void
  className?: string
}

const LIMITS = {
  adults:   { min: 1, max: 30 },
  children: { min: 0, max: 20 },
  rooms:    { min: 1, max: 10 },
} as const

const DEFAULT_CHILD_AGE = 0
const MAX_CHILD_AGE = 11   // 12+ books as an adult

function clamp(n: number, min: number, max: number): number {
  if (Number.isNaN(n)) return min
  return Math.min(max, Math.max(min, Math.round(n)))
}

function childAgeLabel(age: number): string {
  if (age === 0) return '<1 year'
  return `${age} ${age === 1 ? 'year' : 'years'}`
}

interface CounterRowProps {
  label: string
  sublabel?: string
  value: number
  min: number
  max: number
  onChange: (next: number) => void
}

function CounterRow({ label, sublabel, value, min, max, onChange }: CounterRowProps) {
  // Local text state so the field can be cleared/retyped freely; commit on blur/Enter.
  const [text, setText] = useState(String(value))
  useEffect(() => { setText(String(value)) }, [value])

  function commit(raw: string) {
    const next = clamp(Number(raw), min, max)
    onChange(next)
    setText(String(next))
  }

  const btnClass =
    'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-300 text-sm font-medium text-gray-600 transition-colors hover:border-kasa-green-600 hover:text-kasa-green-600 disabled:pointer-events-none disabled:opacity-30'

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        {sublabel && <p className="text-xs text-gray-500">{sublabel}</p>}
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label={`Decrease ${label}`}
          disabled={value <= min}
          onClick={() => onChange(clamp(value - 1, min, max))}
          className={btnClass}
        >
          −
        </button>
        <input
          type="text"
          inputMode="numeric"
          aria-label={label}
          value={text}
          onChange={(e) => setText(e.target.value.replace(/[^0-9]/g, ''))}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commit((e.target as HTMLInputElement).value)
            }
          }}
          className="w-10 rounded-md border border-gray-300 py-1 text-center text-sm font-semibold tabular-nums text-gray-900 focus:outline-none focus:ring-2 focus:ring-kasa-green-500"
        />
        <button
          type="button"
          aria-label={`Increase ${label}`}
          disabled={value >= max}
          onClick={() => onChange(clamp(value + 1, min, max))}
          className={btnClass}
        >
          +
        </button>
      </div>
    </div>
  )
}

function summaryLabel(adults: number, children: number, rooms: number): string {
  const guests = adults + children
  return [
    `${guests} ${guests === 1 ? 'guest' : 'guests'}`,
    `${rooms} ${rooms === 1 ? 'room' : 'rooms'}`,
  ].join(' · ')
}

interface Draft {
  adults: number
  rooms: number
  childAges: number[]
}

export function GuestRoomSelector({
  adults,
  children,
  rooms,
  childAges,
  onChange,
  className,
}: GuestRoomSelectorProps) {
  const [open, setOpen]   = useState(false)
  const [draft, setDraft] = useState<Draft>({
    adults,
    rooms,
    childAges: childAges ?? Array.from({ length: children }, () => DEFAULT_CHILD_AGE),
  })

  // Re-seed the draft from props each time the popover opens.
  useEffect(() => {
    if (!open) return
    setDraft({
      adults,
      rooms,
      childAges: childAges ?? Array.from({ length: children }, () => DEFAULT_CHILD_AGE),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Commit on any close (Done or click-away); editing the draft never closes.
  function handleOpenChange(next: boolean) {
    if (!next) {
      onChange({
        adults:     draft.adults,
        children:   draft.childAges.length,
        rooms:      draft.rooms,
        child_ages: draft.childAges,
      })
    }
    setOpen(next)
  }

  function setChildrenCount(next: number) {
    setDraft((d) => {
      const cur = d.childAges
      if (next > cur.length) {
        return {
          ...d,
          childAges: [...cur, ...Array.from({ length: next - cur.length }, () => DEFAULT_CHILD_AGE)],
        }
      }
      return { ...d, childAges: cur.slice(0, next) }
    })
  }

  function setChildAge(index: number, age: number) {
    setDraft((d) => ({
      ...d,
      childAges: d.childAges.map((a, i) => (i === index ? age : a)),
    }))
  }

  return (
    <Popover.Root open={open} onOpenChange={handleOpenChange}>
      <Popover.Trigger asChild>
        <button
          type="button"
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-left text-sm text-gray-900',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kasa-green-500 focus-visible:ring-offset-1',
            'hover:border-gray-400 transition-colors',
            className,
          )}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 shrink-0 text-gray-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <span className="flex-1 truncate">{summaryLabel(adults, children, rooms)}</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 shrink-0 text-gray-400"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={6}
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="z-50 max-h-[70vh] w-72 overflow-y-auto rounded-xl border border-gray-200 bg-white p-4 shadow-xl"
        >
          <div className="divide-y divide-gray-100">
            <CounterRow
              label="Adults"
              value={draft.adults}
              min={LIMITS.adults.min}
              max={LIMITS.adults.max}
              onChange={(n) => setDraft((d) => ({ ...d, adults: n }))}
            />
            <CounterRow
              label="Children"
              sublabel="Ages 0–11"
              value={draft.childAges.length}
              min={LIMITS.children.min}
              max={LIMITS.children.max}
              onChange={setChildrenCount}
            />

            {/* Per-child age */}
            {draft.childAges.length > 0 && (
              <div className="space-y-2 py-3">
                {draft.childAges.map((age, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <label htmlFor={`child-age-${i}`} className="text-sm text-gray-600">
                      Child {i + 1} age
                    </label>
                    <select
                      id={`child-age-${i}`}
                      value={age}
                      onChange={(e) => setChildAge(i, Number(e.target.value))}
                      className="rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-kasa-green-500"
                    >
                      {Array.from({ length: MAX_CHILD_AGE + 1 }, (_, a) => (
                        <option key={a} value={a}>{childAgeLabel(a)}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}

            <CounterRow
              label="Rooms"
              value={draft.rooms}
              min={LIMITS.rooms.min}
              max={LIMITS.rooms.max}
              onChange={(n) => setDraft((d) => ({ ...d, rooms: n }))}
            />
          </div>

          <Popover.Close asChild>
            <button
              type="button"
              className="mt-3 w-full rounded-md bg-kasa-green-600 py-2 text-sm font-medium text-white hover:bg-kasa-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kasa-green-600 focus-visible:ring-offset-1"
            >
              Done
            </button>
          </Popover.Close>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
